import React, { useRef, useEffect, memo } from 'react';
import { motion, useInView } from 'framer-motion';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { MeshDistortMaterial, Sphere } from '@react-three/drei';

// ============================================================================
// CONFIGURATION CONSTANTS
// ============================================================================

// Colors
const COLOR_CYAN = '#00f0ff';
const COLOR_MAGENTA = '#ff00aa';

// Sphere settings
const SPHERE_RADIUS = 1.5;
const SPHERE_SEGMENTS = 64;
const SPHERE_DISTORT = 0.4;
const SPHERE_SPEED = 2;

// Rotation speeds
const ROTATION_SPEED_X = 0.2;
const ROTATION_SPEED_Y = 0.3;

// Camera settings
const CAMERA_POSITION = [0, 0, 4];
const CAMERA_FOV = 50;

// ============================================================================
// COMPONENTS
// ============================================================================

// Cleanup helper for the scene
function SceneCleanup() {
  const { gl, scene } = useThree();

  useEffect(() => {
    return () => {
      scene.traverse((object) => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach((mat) => mat.dispose());
          } else {
            object.material.dispose();
          }
        }
      });
      gl.dispose();
    };
  }, [gl, scene]);

  return null;
}

// Animated distorted sphere with proper cleanup
function DistortedSphere() {
  const meshRef = useRef();
  const geometryRef = useRef();
  const materialRef = useRef();

  useEffect(() => {
    return () => {
      geometryRef.current?.dispose();
      materialRef.current?.dispose();
    };
  }, []);

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.x = state.clock.elapsedTime * ROTATION_SPEED_X;
      meshRef.current.rotation.y = state.clock.elapsedTime * ROTATION_SPEED_Y;
    }
  });

  return (
    <Sphere ref={meshRef} args={[SPHERE_RADIUS, SPHERE_SEGMENTS, SPHERE_SEGMENTS]}>
      <MeshDistortMaterial
        ref={materialRef}
        color={COLOR_CYAN}
        attach="material"
        distort={SPHERE_DISTORT}
        speed={SPHERE_SPEED}
        roughness={0.2}
        metalness={0.8}
      />
    </Sphere>
  );
}

const stats = [
  { value: '99+', label: 'Languages' },
  { value: 'Real-time', label: 'Transcription' },
  { value: '100%', label: 'Private' },
];

// Main About component - wrapped in React.memo to prevent unnecessary re-renders
const About = memo(function About() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="about" className="snap-section relative min-h-screen flex items-center py-20">
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-dark via-dark/80 to-transparent z-0" />

      <div className="max-w-[1800px] mx-auto px-6 md:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <p className="section-label">About NaLiVox</p>

            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Your real-time AI assistant for{' '}
              <span className="gradient-text">high-stakes conversations</span>
            </h2>

            <p className="text-gray-400 text-lg leading-relaxed mb-6">
              <strong>Ace your interviews.</strong> NaLiVox transcribes your video calls in real-time
              and provides intelligent suggestions based on your CV and notes. Stay focused on the
              conversation while AI handles the details.
            </p>

            <p className="text-gray-400 text-lg leading-relaxed mb-6">
              <strong>Supercharge your meetings.</strong> Ask the AI anything about the ongoing discussion:
              "What numbers were mentioned?", "Summarize the last 5 minutes", "What action items came up?"
              — it understands full context.
            </p>

            <p className="text-gray-500 leading-relaxed mb-10">
              <strong>Works everywhere.</strong> Teams, Zoom, Meet, Discord — just share your screen.
              No bots joining your call, no extensions, no installs.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                >
                  <div className="text-3xl md:text-4xl font-display font-bold gradient-text">
                    {stat.value}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-gray-500 mt-1">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* 3D Sphere */}
          <motion.div
            className="h-[400px] lg:h-[500px]"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <Canvas
              camera={{ position: CAMERA_POSITION, fov: CAMERA_FOV }}
              dpr={[1, 1.5]}
              frameloop={isInView ? 'always' : 'never'}
            >
              <SceneCleanup />
              <ambientLight intensity={0.3} />
              <pointLight position={[10, 10, 10]} color={COLOR_CYAN} intensity={1} />
              <pointLight position={[-10, -10, 10]} color={COLOR_MAGENTA} intensity={0.5} />
              <DistortedSphere />
            </Canvas>
          </motion.div>
        </div>
      </div>
    </section>
  );
});

export default About;
